/**
 * Activity / P-Wave Indicators - Phase 3 wiring.
 *
 * Live data source: GET /v1/activity/early-warning
 * Server response: `{ indicators: EarlyWarningIndicator[] }` per
 * apps/api-controller/src/modules/activity/handlers.ts:listEarlyWarning.
 *
 * The six detector cards correspond to the `indicatorType` enum on the
 * server (`apps/api-controller/src/shared/database/schema/activity.ts:121`).
 * Each card shows: live fires (24h count from the live response),
 * last-fired timestamp, status (fired vs idle).
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  Radio, Network, ShieldOff, KeyRound, Clock, Database, Activity as ActivityIcon, Loader2, AlertTriangle, ArrowUpRight, Sparkles,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { listEarlyWarning, analyzeEarlyWarning, type EarlyWarningIndicator } from '@/lib/api';

type IndicatorType = EarlyWarningIndicator['indicatorType'];

interface DetectorMeta {
  id: IndicatorType;
  icon: React.ComponentType<{ className?: string }>;
  name: string;
  desc: string;
}

const DETECTOR_META: DetectorMeta[] = [
  { id: 'schema_mapping',           icon: Network,      name: 'Schema Mapping Recon',         desc: 'Detects information_schema / pg_class scans typical of attacker reconnaissance' },
  { id: 'audit_log_disable_attempt',icon: ShieldOff,    name: 'Audit Log Disable Attempt',    desc: 'Watches for pgaudit unset, MSSQL Extended Events STOP, Oracle UNIFIED_AUDIT_TRAIL truncate' },
  { id: 'credential_creation',      icon: KeyRound,     name: 'Credential Creation Watch',    desc: 'CREATE USER / CREATE LOGIN / GRANT ALL events outside change windows' },
  { id: 'off_pattern_query',        icon: Clock,        name: 'Off-Pattern Query (hour + IP)',desc: 'Per-actor baselines flag queries from unexpected hours or IPs' },
  { id: 'cold_table_burst',         icon: Database,     name: 'Cold-Table Burst',             desc: 'Tables not touched in 90 days suddenly seeing high read traffic' },
  { id: 'reconnaissance_burst',     icon: ActivityIcon, name: 'Mass Recon Burst',             desc: 'Many cold tables touched by one actor in a sliding window (often precedes mass export)' },
];

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'in the future';
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export function ActivityPwave() {
  const [indicators, setIndicators] = useState<EarlyWarningIndicator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeStatus, setAnalyzeStatus] = useState<string | null>(null);

  // Wave 2 (2026-05-27) - surface POST /v1/activity/early-warning/analyze.
  // Runs all 6 P-wave detectors over the last 24h. Persists any
  // indicators fired so the timeline below refreshes.
  async function handleAnalyze(): Promise<void> {
    setAnalyzing(true);
    setAnalyzeStatus(null);
    try {
      const result = await analyzeEarlyWarning({ recentHours: 24 });
      const n = Array.isArray(result.findings) ? result.findings.length : 0;
      setAnalyzeStatus(
        n === 0
          ? 'Analysis complete - no P-wave indicators fired in the last 24h.'
          : `Analysis complete - ${n} indicator${n === 1 ? '' : 's'} fired.`,
      );
      await refresh();
    } catch (e) {
      setAnalyzeStatus(e instanceof Error ? `Analysis failed: ${e.message}` : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }

  const refresh = (signal?: AbortSignal) =>
    listEarlyWarning()
      .then((r) => !signal?.aborted && setIndicators(r.indicators))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // Group live indicators by detector type.
  const byType = useMemo(() => {
    const m = new Map<IndicatorType, EarlyWarningIndicator[]>();
    for (const ind of indicators) {
      const arr = m.get(ind.indicatorType) ?? [];
      arr.push(ind);
      m.set(ind.indicatorType, arr);
    }
    return m;
  }, [indicators]);

  const cutoff24h = Date.now() - 24 * 3600 * 1000;
  const fires24hTotal = indicators.filter((i) => new Date(i.firedAt).getTime() >= cutoff24h).length;

  // Most-recent indicator across all detectors - drives the "Most recent
  // fire" stripe at the top of the timeline section.
  const mostRecent = useMemo(
    () => [...indicators].sort((a, b) => new Date(b.firedAt).getTime() - new Date(a.firedAt).getTime()).slice(0, 8),
    [indicators],
  );

  return (
    <PageContainer>
      <PageHeader
        title="P-Wave Indicators"
        subtitle="Earthquake Early Warning for data breaches. Six detectors fire seconds before a breach pattern completes."
        badges={[
          { label: 'Pre-cog', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: `${fires24hTotal} fires (24h)`, variant: fires24hTotal > 0 ? ('warning' as const) : ('success' as const) }]),
        ]}
        actions={
          <Button
            size="sm"
            onClick={() => void handleAnalyze()}
            disabled={analyzing}
            title="Run all 6 P-wave detectors over the last 24h"
            data-action="disc.dam_pwave.a01"
          >
            <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${analyzing ? 'animate-pulse' : ''}`} />
            {analyzing ? 'Analyzing…' : 'Analyze now'}
          </Button>
        }
      />
      {analyzeStatus && (
        <div className="glass-lite rounded-md p-3 mb-3 text-xs text-muted-foreground">
          {analyzeStatus}
        </div>
      )}

      {loading && <LoadingState caption="Loading early-warning indicators…" />}
      {error && !loading && (
        <ErrorState
          title="Failed to load indicators"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {DETECTOR_META.map((d, i) => {
              const fires = byType.get(d.id) ?? [];
              const fires24 = fires.filter((f) => new Date(f.firedAt).getTime() >= cutoff24h).length;
              const lastFired = fires[0]?.firedAt;
              const isFired = fires24 > 0;
              const Icon = d.icon;
              return (
                <motion.div
                  key={d.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <GlassCard className={`p-5 h-full ${isFired ? 'glow-high' : ''}`} shine>
                    <div className="flex items-start justify-between mb-3">
                      <div className={`h-10 w-10 rounded-md flex items-center justify-center ${isFired ? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'}`}>
                        <Icon className={`h-5 w-5 ${isFired ? 'animate-pulse' : ''}`} />
                      </div>
                      <Badge variant={isFired ? 'warning' : 'glass'} className="text-[9px]">
                        {isFired ? 'FIRED' : 'IDLE'}
                      </Badge>
                    </div>
                    <CardTitle className="text-sm mb-1">{d.name}</CardTitle>
                    <CardDescription className="text-xs leading-snug mb-3">{d.desc}</CardDescription>
                    <div className="flex items-end justify-between pt-2 border-t border-border/30">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fires 24h</div>
                        <div className="text-2xl font-display tabular">{fires24}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last</div>
                        <div className="text-xs">{lastFired ? relativeTime(lastFired) : '-'}</div>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>

          <GlassCard className="p-6">
            <CardHeader className="p-0 mb-4">
              <CardTitle>Most recent indicator fires</CardTitle>
              <CardDescription clamp>Live tail across all six detectors - newest first.</CardDescription>
            </CardHeader>
            {mostRecent.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                <Radio className="h-6 w-6 mx-auto mb-2 opacity-40" />
                No early-warning indicators have fired for this tenant yet. As the UEBA engine analyses the activity stream, fires appear here in real time.
              </div>
            ) : (
              <div className="relative pl-6 space-y-3">
                <div className="absolute left-2.5 top-0 bottom-0 w-px bg-border" />
                {mostRecent.map((e, i) => (
                  <motion.div
                    key={e.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="relative flex items-start gap-3"
                  >
                    <span className={`absolute -left-[15px] h-3 w-3 rounded-full mt-1.5 ${
                      e.severity === 'critical' ? 'bg-sev-critical animate-pulse' :
                      e.severity === 'high' ? 'bg-sev-high' :
                      e.severity === 'medium' ? 'bg-sev-medium' : 'bg-sev-low'
                    }`} />
                    <div className="flex-1 glass-lite p-3 rounded-md">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-muted-foreground">{new Date(e.firedAt).toLocaleString()}</span>
                        <Badge variant="warning" className="text-[9px] capitalize">{e.indicatorType.replace(/_/g, ' ')}</Badge>
                        {e.actorId && <code className="text-xs font-mono">{e.actorId}</code>}
                        <Badge
                          variant={e.severity as 'critical' | 'high' | 'medium' | 'low'}
                          className="text-[9px] ml-auto"
                        >
                          {e.severity}
                        </Badge>
                      </div>
                      {e.relatedEventCount > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {e.relatedEventCount.toLocaleString('en-IN')} related events captured in the firing window
                        </p>
                      )}
                      {/* P1-5 (2026-05-26) - jump to Decisions filtered
                          by the firing actor. Indicators with no actorId
                          (system-level) get a plain link to Decisions. */}
                      <div className="mt-1.5">
                        <Link
                          to="/detection/decisions"
                          search={(e.actorId ? { q: e.actorId } : {}) as never}
                          className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5"
                          title={e.actorId ? `See decisions involving ${e.actorId}` : 'See recent decisions'}
                        >
                          Decisions <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}
